import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { aggregateCMVs, getNiceDate, numRound } from '@iotv/datamodel-core';
import { Alert, MenuItem, Select, Stack } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { v4 } from 'uuid';
import { ContainerArbitrator } from '../../../ArbitrationObjects/ContainerArbitrator/containerArbitrator';
import { StripedDataGrid } from '../../factories/DataGrid';
import { SignalAttributeCell } from '../../factories/DataGrid/cellFormatters/rangeColors';
import { SimpleIOModal } from '../../io/VOBbased/SimpleIOModal';
import { IOType } from '../../io/types';
import { CoverageReportContext, getValidation, getValidationMessage } from '../Contexts';
import { NetworkCoverageContext } from '../NetworkCoverageContext';
import { getCoverageReportSk } from '../ResultRetrieval/hooks/useCoverage';
const debug = process.env.REACT_APP_DEBUG && false;
export const ReportViewer = () => {
    // coverage state
    const { state } = useContext(NetworkCoverageContext);
    const validationMap = getValidation(state);
    // all reports state
    const { coverageReportMap, selectedReports: selectedCoverageReportSks } = useContext(CoverageReportContext);
    const getSelectedReports = (coverageReportMap) => {
        const selectedReports = [];
        Object.values(coverageReportMap).forEach((coverageReports) => {
            coverageReports.forEach((report) => {
                if (selectedCoverageReportSks.includes(getCoverageReportSk(report)))
                    selectedReports.push(report);
            });
        });
        return selectedReports;
    };
    const selectedReports = getSelectedReports(coverageReportMap);
    const [reportGatheringStrategy, setReportGatheringStrategy] = useState('separateByGateway');
    const cmvsToReportRow = (cmvs) => {
        return {
            'RSSI Min': cmvs.rssi.min,
            'RSSI Mean': Math.round(cmvs.rssi.mean * 100) / 100,
            'RSSI Max': cmvs.rssi.max,
            'RSSI Delta': numRound(Math.sqrt(cmvs.rssi.sqDiffFromMean) / cmvs.rssi.cn, 2),
            'RSSI Std. Dev.': numRound(Math.sqrt(cmvs.rssi.sqDiffFromMean / cmvs.rssi.cn), 2),
            'SNR Min': cmvs.snr.min,
            'SNR Mean': numRound(cmvs.snr.mean, 2),
            'SNR Max': cmvs.snr.max,
            'SNR Std. Dev.': numRound(Math.sqrt(cmvs.snr.sqDiffFromMean / cmvs.snr.cn), 2),
            'SF Min': cmvs.sf.min,
            'SF Mean': numRound(cmvs.sf.mean, 2),
            'SF Max': cmvs.sf.max,
            'SF Std. Dev.': numRound(Math.sqrt(cmvs.sf.sqDiffFromMean / cmvs.sf.cn), 2),
            'ESP Min': cmvs.esp.min ? numRound(cmvs.esp.min, 2) : undefined,
            'ESP Mean': numRound(cmvs.esp.mean, 2),
            'ESP Max': cmvs.esp.max ? numRound(cmvs.esp.max, 2) : undefined,
            'ESP Std. Dev.': numRound(Math.sqrt(cmvs.esp.sqDiffFromMean / cmvs.esp.cn), 2),
        };
    };
    const getKPIRow = (geoObj) => {
        return {
            espKpi: geoObj.espKpi,
            espGood: geoObj.espGood,
            espOk: geoObj.espOk,
            espPoor: geoObj.espPoor,
            DLPerDay: geoObj.DLPerDay,
            droppedULPercentKpiGood: geoObj.droppedULPercentKpiGood,
            droppedULPercentKpiOk: geoObj.droppedULPercentKpiOk,
            droppedULPercentKpiPoor: geoObj.droppedULPercentKpiPoor,
            ULPerDay: geoObj.ULPerDay,
        };
    };
    /**
     * Gets one row for every combination of area and gateway.
     *
     * Another way of saying it is we get one row summarising the metrics for a gateway, of the data from that gateway that was sourced from Area
     *
     * Really doesn't matter if you only have one gateway nearby, which is why this is the default, but if you have more than one, the aggregated fn might be more to your liking
     */
    const getReportRowsSeperatedGateways = (reports) => {
        debug && console.log('Getting reports', reports);
        const rows = [];
        for (const report of reports) {
            Object.values(report.Networks).forEach((networkStats) => {
                Object.entries(networkStats.GatewayStats).forEach(([gatewayID, gatewayCMVs]) => {
                    rows.push({
                        id: v4(),
                        Area: report.GeoObject.name ?? report.GeoObject.sk,
                        geoObjType: report.GeoObject.type,
                        Network: networkStats.Network.replaceAll(/_/g, ' '),
                        Gateway: networkStats.Gateways.find((g) => g.id === gatewayID) ?? gatewayID,
                        From: getNiceDate(report.timePeriod.fromDateMs),
                        To: getNiceDate(report.timePeriod.toDateMs),
                        On: getNiceDate(new Date(report.created).valueOf()),
                        Count: report.rowCount.filtered,
                        Dropped: typeof gatewayCMVs.dropped === 'number' ? gatewayCMVs.dropped : undefined,
                        droppedPercent: typeof gatewayCMVs.dropped === 'number' && gatewayCMVs.dropped !== undefined
                            ? Math.round((gatewayCMVs.dropped / (report.rowCount.filtered + gatewayCMVs.dropped)) * 100)
                            : undefined,
                        ...cmvsToReportRow(gatewayCMVs),
                        ...getKPIRow(report.GeoObject),
                        // 'ESP Gap': report.GeoObject.targetESP ? Math.round((gatewayCMVs.esp.mean + report.GeoObject.targetESP * -1) * 100) / 100 : undefined,
                        // 'ESP Target': report.GeoObject.targetESP,
                    });
                });
            });
        }
        return rows;
    };
    /**
     * I think this means that, for each area, all of the metrics in that area, no matter the gatway, will be included in a single line.
     * By default, we get seperate metrics for each combination of gateway and area, if a device in an area ends up connecting to more than one gateway.
     *
     * To accomplish this, theres an additional step for each coverage report where we
     *
     * INFO: (ADH) to the best of my knowledge this still splits messages out by network, but I've never observed nor tested this
     */
    const getReportRowsAggregatedGateways = (reports) => {
        debug && console.log('Getting reports', reports);
        const rows = [];
        for (const report of reports) {
            Object.values(report.Networks).forEach((networkStats) => {
                // Before creating the row, aggregated all gateways within this network into one CMVarAggrObj for each metric
                const aggrMetrics = {};
                ['esp', 'snr', 'rssi', 'sf', 'power'].forEach((key) => {
                    const CMVsToAggr = Object.values(networkStats.GatewayStats)
                        .map((gatewayCMVs) => gatewayCMVs[key])
                        .filter((gatewayCMV) => !!gatewayCMV);
                    debug && console.log(CMVsToAggr);
                    // RSE Commented out, only one param in aggregateCMVs (@iotv/datamodel-core)
                    if (CMVsToAggr.length)
                        aggrMetrics[key] = aggregateCMVs(CMVsToAggr /*key === "dropped"*/);
                });
                aggrMetrics['dropped'] = Object.values(networkStats.GatewayStats).reduce((total, metrics) => total + (typeof metrics.dropped === 'number' ? metrics.dropped : 0), 0);
                rows.push({
                    id: v4(),
                    Area: report.GeoObject.name ?? report.GeoObject.sk,
                    geoObjType: report.GeoObject.type,
                    Network: networkStats.Network.replaceAll(/_/g, ' '),
                    Gateway: 'Aggregated',
                    From: getNiceDate(report.timePeriod.fromDateMs),
                    To: getNiceDate(report.timePeriod.toDateMs),
                    On: getNiceDate(new Date(report.created).valueOf()),
                    Count: report.rowCount.filtered,
                    Dropped: aggrMetrics.dropped,
                    droppedPercent: aggrMetrics.dropped !== undefined ? Math.round((aggrMetrics.dropped / (report.rowCount.filtered + aggrMetrics.dropped)) * 100) : undefined,
                    ...cmvsToReportRow(aggrMetrics),
                    ...getKPIRow(report.GeoObject),
                });
            });
        }
        return rows;
    };
    const reportRows = useMemo(() => {
        switch (reportGatheringStrategy) {
            case 'separateByGateway':
                return getReportRowsSeperatedGateways(selectedReports);
            case 'aggregate':
                return getReportRowsAggregatedGateways(selectedReports);
        }
    }, [selectedReports, reportGatheringStrategy]);
    const reportColumns = [
        { field: 'Area', headerName: 'Area', width: 200 },
        { field: 'geoObjType', headerName: 'Type', width: 80 },
        { field: 'Network', headerName: 'Network', width: 120 },
        {
            field: 'Gateway',
            headerName: 'Gateway',
            width: 120,
            renderCell: (params) => {
                if (typeof params.value === 'object') {
                    const object = params.value;
                    return _jsx(NavLink, { to: ContainerArbitrator.getCanonicalContainerLinkForObject({ vob: object }), children: object.name });
                }
                return params.value;
            },
        },
        { field: 'From', headerName: 'From', width: 160 },
        { field: 'To', headerName: 'To', width: 160 },
        { field: 'On', headerName: 'On', width: 160 },
        { field: 'Count', headerName: 'Received', width: 85 },
        { field: 'Dropped', headerName: 'Dropped', width: 90 },
        { field: 'droppedPercent', headerName: '% Dropped', width: 90 },
        { field: 'RSSI Min', headerName: 'Min', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('rssi', params.value) },
        { field: 'RSSI Mean', headerName: 'Mean', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('rssi', params.value) },
        { field: 'RSSI Max', headerName: 'Max', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('rssi', params.value) },
        { field: 'RSSI Delta', headerName: 'Delta', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('rssi', params.value) },
        { field: 'RSSI Std. Dev.', headerName: 'Std. Dev.', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('rssi', params.value) },
        { field: 'SNR Min', headerName: 'Min', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('snr', params.value) },
        { field: 'SNR Mean', headerName: 'Mean', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('snr', params.value) },
        { field: 'SNR Max', headerName: 'Max', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('snr', params.value) },
        { field: 'SNR Std. Dev.', headerName: 'Std. Dev.', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('snr', params.value) },
        { field: 'SF Min', headerName: 'Min', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('sf', params.value) },
        { field: 'SF Mean', headerName: 'Mean', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('sf', params.value) },
        { field: 'SF Max', headerName: 'Max', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('sf', params.value) },
        { field: 'SF Std. Dev.', headerName: 'Std. Dev.', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('sf', params.value) },
        { field: 'ESP Min', headerName: 'Min', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('esp', params.value) },
        { field: 'ESP Mean', headerName: 'Mean', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('esp', params.value) },
        { field: 'ESP Max', headerName: 'Max', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('esp', params.value) },
        { field: 'ESP Std. Dev.', headerName: 'Std. Dev.', width: 80, align: 'center', renderCell: (params) => SignalAttributeCell('esp', params.value) },
        { field: 'espKpi', headerName: 'ESP KPI', width: 70, align: 'center' },
        { field: 'espGood', headerName: 'ESP Good', width: 80, align: 'center', valueFormatter: (v) => `${v}%` },
        { field: 'espOk', headerName: 'ESP OK', width: 70, align: 'center', valueFormatter: (v) => `${v}%` },
        { field: 'espPoor', headerName: 'ESP Poor', width: 80, align: 'center', valueFormatter: (v) => `${v}%` },
        { field: 'droppedULPercentKpiGood', headerName: 'UL KPI Good', width: 100, align: 'center', valueFormatter: (v) => `${v}%` },
        { field: 'droppedULPercentKpiOk', headerName: 'UL KPI OK', width: 90, align: 'center', valueFormatter: (v) => `${v}%` },
        { field: 'droppedULPercentKpiPoor', headerName: 'UL KPI Poor', width: 100, align: 'center', valueFormatter: (v) => `${v}%` },
        { field: 'DLPerDay', headerName: 'DL Per Day', width: 100, align: 'center' },
        { field: 'ULPerDay', headerName: 'UL Per Day', width: 100, align: 'center' },
    ];
    const reportColumnGrouping = [
        {
            groupId: 'RSSI',
            children: [{ field: 'RSSI Min' }, { field: 'RSSI Mean' }, { field: 'RSSI Max' }, { field: 'RSSI Delta' }, { field: 'RSSI Std. Dev.' }],
        },
        {
            groupId: 'SNR',
            children: [{ field: 'SNR Min' }, { field: 'SNR Mean' }, { field: 'SNR Max' }, { field: 'SNR Std. Dev.' }],
        },
        {
            groupId: 'SF',
            children: [{ field: 'SF Min' }, { field: 'SF Mean' }, { field: 'SF Max' }, { field: 'SF Std. Dev.' }],
        },
        {
            groupId: 'ESP',
            children: [{ field: 'ESP Min' }, { field: 'ESP Mean' }, { field: 'ESP Max' }, { field: 'ESP Gap' }, { field: 'ESP Target' }, { field: 'ESP Std. Dev.' }],
        },
    ];
    const dataGridProps = {
        columns: reportColumns,
        columnGroupingModel: reportColumnGrouping,
        rows: reportRows,
        rowHeight: 32,
        density: 'compact',
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        sx: {
            [`& .MuiDataGrid-cell`]: {
                ['&[data-field^="RSSI"], &[data-field^="SNR"], &[data-field^="SF"], &[data-field^="ESP"]']: {
                    padding: 0,
                },
            },
        },
    };
    const objectTransformer = (object) => {
        const { type, id, ...others } = object;
        const keys = Object.keys(others);
        return {
            columnIndexedKeys: keys,
            row: keys.map((k) => {
                if (typeof object[k] === 'object') {
                    return object[k] === null ? '' : (object[k].name ?? JSON.stringify(object[k]));
                }
                else {
                    return object[k];
                }
            }),
        };
    };
    return (_jsxs(Stack, { spacing: 1, children: [_jsxs(Stack, { direction: "row", gap: 1, children: [_jsxs(Select, { size: "small", value: reportGatheringStrategy, onChange: (e) => setReportGatheringStrategy(e.target.value), children: [_jsx(MenuItem, { value: "aggregate", children: "Aggregate" }), _jsx(MenuItem, { value: "separateByGateway", children: "Separate by Gateway" })] }), reportRows && (_jsx(SimpleIOModal, { columnIndexedKeys: reportColumns.map((c) => c.field),
                        plainObjects: reportRows.map((r) => {
                            return { ...r, type: 'ReportRow', id: r.id.toString() };
                        }),
                        objectToRowFunction: objectTransformer,
                        initialIOType: IOType.EXPORT,
                        allowIOTypeToggle: false,
                        parentHasRendered: () => { },
                        setPlainObjectsInParent: () => { } }))] }), _jsx(StripedDataGrid, { ...dataGridProps }, JSON.stringify(reportRows)), getValidationMessage(validationMap).replace(/\s/g, '') != '' && _jsx(Alert, { severity: "error", children: `${getValidationMessage(validationMap)}` })] }));
};
